import { type RefObject, useEffect, useRef } from 'react'

type Options = {
  /** Element whose size changes should also trigger `callback`. */
  observe?: RefObject<Element | null>
}

/**
 * Runs `callback` in a `requestAnimationFrame` — after paint, not in the layout phase — once on
 * mount and again whenever anything on the page scrolls, the window resizes, or `options.observe`
 * changes size. Triggers landing in the same frame are coalesced into a single call.
 *
 * Intended for DOM measurement that has to stay in sync with layout — reading
 * `getBoundingClientRect` and writing styles back — where there is no React state to key off.
 * `callback` is always invoked at its latest identity, so it does not need to be memoized.
 *
 * `options.observe` is resolved when the effect runs, so a ref attached later is not observed.
 */
export const useSyncOnLayoutChange = (callback: () => void, { observe }: Options = {}) => {
  const callbackRef = useRef(callback)
  useEffect(() => {
    callbackRef.current = callback
  })

  useEffect(() => {
    let frame = 0
    const run = () => {
      frame = 0
      callbackRef.current()
    }
    const schedule = () => {
      if (frame === 0) frame = requestAnimationFrame(run)
    }

    schedule()
    // Capture phase: scroll events from a nested scroll container do not bubble, so a
    // document-level listener only sees them while capturing.
    document.addEventListener('scroll', schedule, true)
    window.addEventListener('resize', schedule)
    const resizeObserver = new ResizeObserver(schedule)
    const observed = observe?.current
    if (observed) resizeObserver.observe(observed)

    return () => {
      document.removeEventListener('scroll', schedule, true)
      window.removeEventListener('resize', schedule)
      resizeObserver.disconnect()
      if (frame !== 0) cancelAnimationFrame(frame)
    }
  }, [observe])
}
